import { generateUUID } from '../utils/uuid';

// Device ID management
const getDeviceId = (): string => {
  let deviceId = localStorage.getItem('deviceId');
  if (!deviceId) {
    deviceId = generateUUID();
    localStorage.setItem('deviceId', deviceId);
  }
  return deviceId;
};

export interface UserRegistrationData {
  email: string;
  marketingConsent: boolean;
  prayerReminders: boolean;
  newsletter: boolean;
  // Google OAuth data
  googleId?: string;
  name?: string;
  picture?: string;
}

export interface SubscriptionStatus {
  hasAccess: boolean;
  accessType: 'none' | 'premium' | 'expired';
  trialEligible: boolean;
  user?: {
    email: string;
    deviceId: string;
    registeredAt: string;
    googleId?: string;
    name?: string;
  };
  subscription?: {
    platform: string;
    isActive: boolean;
    expiresAt?: string;
  };
  checkedAt: string;
}

const API_BASE = '/.netlify/functions';

export const subscriptionService = {
  // Register user with email
  registerUser: async (data: UserRegistrationData): Promise<void> => {
    const response = await fetch(`${API_BASE}/register-user`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        ...data,
        deviceId: getDeviceId()
      })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to register user');
    }

    const result = await response.json();

    // Store email locally for future API calls
    localStorage.setItem('userEmail', data.email);

    return result;
  },

  // Check subscription status with Google ID support
  checkSubscriptionStatus: async (options?: {
    email?: string;
    googleId?: string;
  }): Promise<SubscriptionStatus> => {
    const userEmail = options?.email || localStorage.getItem('userEmail');
    const googleId = options?.googleId;
    const deviceId = getDeviceId();

    const params = new URLSearchParams();
    if (userEmail) params.append('email', userEmail);
    if (googleId) params.append('googleId', googleId);
    params.append('deviceId', deviceId);

    const response = await fetch(`${API_BASE}/subscription-status?${params}`);

    if (!response.ok) {
      console.error('Failed to check subscription status');
      // Return default state if API fails
      return {
        hasAccess: false,
        accessType: 'none',
        trialEligible: true,
        checkedAt: new Date().toISOString()
      };
    }

    return response.json();
  },

  // Get stored user email
  getUserEmail: (): string | null => {
    return localStorage.getItem('userEmail');
  },

  // Check if user has provided email
  hasUserEmail: (): boolean => {
    return !!localStorage.getItem('userEmail');
  },

  // Register Google authenticated user
  registerGoogleUser: async (googleUser: {
    id: string;
    email: string;
    name: string;
    picture?: string;
  }, preferences?: {
    marketingConsent?: boolean;
    prayerReminders?: boolean;
    newsletter?: boolean;
  }): Promise<void> => {
    const data: UserRegistrationData = {
      email: googleUser.email,
      googleId: googleUser.id,
      name: googleUser.name,
      picture: googleUser.picture,
      marketingConsent: preferences?.marketingConsent || false,
      prayerReminders: preferences?.prayerReminders || true,
      newsletter: preferences?.newsletter || false,
    };

    const response = await fetch(`${API_BASE}/register-user`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        ...data,
        deviceId: getDeviceId()
      })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to register user');
    }

    const result = await response.json();

    // Store email locally for backward compatibility
    localStorage.setItem('userEmail', googleUser.email);

    return result;
  },

  // Get user data by Google ID or email
  getUserData: async (googleId?: string, email?: string): Promise<any> => {
    const params = new URLSearchParams();
    if (googleId) params.append('googleId', googleId);
    if (email) params.append('email', email);
    params.append('deviceId', getDeviceId());

    const response = await fetch(`${API_BASE}/get-user?${params}`);

    if (!response.ok) {
      if (response.status === 404) {
        return null; // User not found
      }
      throw new Error('Failed to get user data');
    }

    return response.json();
  },

  // Get device ID (useful for debugging)
  getDeviceId: (): string => {
    return getDeviceId();
  }
};